import { computed, makeAutoObservable } from 'mobx';
import {
    apiGetCompetitionsRequest,
    ApiGetCompetitionsResponse200Type,
    ApiGetCompetitionsResponseType,
} from 'src/api_openapi/v2/trading-search/apiGetCompetitions';
import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import {
    BaseTreeType,
    CompetitionType,
    deserializeNodeValue,
    EventTreeType,
    PER_PAGE,
    serializeNodeValue,
} from './utils';
import { EventStoreState } from './EventStore.state';

export class CompetitionStoreState {
    private readonly allCompetitionsResource: AutoMap<number, Resource<ApiGetCompetitionsResponseType | null>>;
    public readonly eventStore: AutoMap<string, EventStoreState>;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string,
        private readonly loadedNodes: () => Set<string>,
        private readonly readOnlyMode: boolean
    ) {
        this.allCompetitionsResource = new AutoMap((page) => {
            return new Resource(() => this.fetchCompetitions(page));
        });

        this.eventStore = new AutoMap((competitionId) => {
            return new EventStoreState(common, this.sportId, competitionId, this.loadedNodes, this.readOnlyMode);
        });

        makeAutoObservable(this);
    }

    private async fetchCompetitions(page: number): Promise<ApiGetCompetitionsResponseType> {
        const getCompetitionsReponse = await apiGetCompetitionsRequest(
            `${this.common.appEnvParams.gatewayUrl}`,
            {
                sportId: this.sportId,
                page,
                pageSize: PER_PAGE,
                active: true,
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );
        return getCompetitionsReponse;
    }

    private getCompetitionsPage = (page: number): ApiGetCompetitionsResponse200Type | 'loading' | 'error' => {
        const res = this.allCompetitionsResource.get(page).get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error' || res.value === null || res.value.status !== 200) {
            return 'error';
        }

        return res.value.body;
    };

    @computed public get getCompetitions(): BaseTreeType[] | 'loading' | 'error' {
        const fetchedItems: Array<CompetitionType> = [];
        const resFirstPage = this.getCompetitionsPage(1);

        if (resFirstPage === 'loading') {
            return 'loading';
        }

        if (resFirstPage === 'error') {
            return 'error';
        }

        resFirstPage.items.map((item) => {
            fetchedItems.push(item);
        });

        // load other pages
        const pageCount = resFirstPage.pagination.pageCount;
        if (pageCount > 1) {
            for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
                const resAnotherPage = this.getCompetitionsPage(currentPage);

                if (resAnotherPage === 'loading') {
                    return 'loading';
                }

                if (resAnotherPage === 'error') {
                    return 'error';
                }

                resAnotherPage.items.map((item) => {
                    fetchedItems.push(item);
                });
            }
        }

        return fetchedItems.map((competition) => {
            let events: Array<EventTreeType> = [];

            if (this.getLoadedCompetitionIds.includes(competition.competitionId)) {
                const data = this.eventStore.get(competition.competitionId).getEvents;

                if (data === 'error') {
                    // handle error
                    // Toast ?
                }

                if (data !== 'loading' && data !== 'error') {
                    events = data;
                }
            }

            const serializedValue = serializeNodeValue({
                type: 'competition',
                sportId: this.sportId,
                competitionId: competition.competitionId,
            });
            return {
                id: serializedValue,
                value: serializedValue,
                label: competition.name,
                showCheckbox: !this.readOnlyMode,
                children: events,
            };
        });
    }

    @computed public get getLoadedCompetitionIds(): string[] {
        const loadedCompetitionIds: Array<string> = [];
        for (const nodeValue of this.loadedNodes()) {
            const loadedDeserialized = deserializeNodeValue(nodeValue);
            if (loadedDeserialized !== null && loadedDeserialized.type === 'competition') {
                // only current context
                if (loadedDeserialized.sportId === this.sportId) {
                    loadedCompetitionIds.push(loadedDeserialized.competitionId);
                }
            }
        }
        return loadedCompetitionIds;
    }

    @computed public get isLoading(): boolean {
        if (this.getCompetitions === 'loading') {
            return true;
        }

        let isLoading = false;
        for (const competitionId of this.getLoadedCompetitionIds) {
            if (this.eventStore.get(competitionId).isLoading === true) {
                isLoading = true;
            }
        }
        return isLoading;
    }
}
